import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import DynamicConfigsResource, { AppConfigEnum, IDynamicConfigs } from '@/api/dynamicConfigs'
import store from '@/store'
import {
  LinkedinIntegrationType
} from '@/views/settings/integrations/job-boards/linked-in-job-board/models/linkedin-integration-type'
import {
  LinkedinIntegration
} from '@/views/settings/integrations/job-boards/linked-in-job-board/models/linkedin-integration'
import {
  CompanyThirdPartyIntegrationsItem,
  CompanyThirdPartyIntegrationsTypeEnum
} from '@/views/settings/integrations/hrms-integration/models/CompanyThirdPartyIntegrationsItem'

@Module({ dynamic: true, name: 'company', store, namespaced: true })
class Company extends VuexModule {
  public haveLanguageSupport = false
  public hasRequisitionsFeature = false
  public haveCvParseAndScoreSupport = false
  public companyLogo = ''
  public emailSignature = ''
  public haveReportsBetaTesting = false
  public linkedinIntegration: LinkedinIntegration = {
    linkedinCompanyId: null,
    isLinkedinIntegrationActive: true,
    linkedinIntegrationType: LinkedinIntegrationType.NONE
  }

  public jobTargetCompanyId: number = 0;
  public hasJobTargetIntegration = false

  public hrmsIntegrationType = '';
  public hrmsIntegrations: CompanyThirdPartyIntegrationsItem[] = []
  public haveMeetingBotRecordingIntegration = false

  /**
  * used to auto check "Record and take notes of meeting" checkbox while sending Calender Invite
  */
  public haveMeetingBotAutoRecordFlag = false
  public isRequisitionEnabled: boolean = false

  // TODO: remove this mutation after requisition is enabled for all companies
  @Mutation
  public SET_FEATURE_REQUISITIONS(featureFlag: boolean) {
    this.hasRequisitionsFeature = featureFlag
  }

  @Mutation
  public SET_REQUISITION_ENABLE_STATUS(isRequisitionEnabled: boolean) {
    this.isRequisitionEnabled = isRequisitionEnabled
  }

  @Mutation
  public SET_LANGUAGE_SUPPORT(support: boolean) {
    this.haveLanguageSupport = support
  }

  @Mutation
  public SET_CV_PARSE_AND_SCORE_SUPPORT(support: boolean) {
    this.haveCvParseAndScoreSupport = support
  }

  @Mutation
  public SET_REPORTS_BETA_TESTING(support: boolean) {
    this.haveReportsBetaTesting = support
  }

  @Mutation
  private SET_LOGO(logo: string) {
    this.companyLogo = logo
  }

  @Mutation
  private SET_COMPANY_EMAIL_SIGNATURE(emailSignature: string) {
    this.emailSignature = emailSignature
  }

  @Mutation
  public ENABLE_JOB_TARGET_INTEGRATION(status: boolean) {
    this.hasJobTargetIntegration = status || process.env.NODE_ENV !== 'production'
  }

  @Mutation
  private SET_JOB_TARGET_COMPANY_ID(jobTargetCompanyId: number) {
    this.jobTargetCompanyId = jobTargetCompanyId
  }

  @Mutation
  public SET_UP_LINKEDIN_INTEGRATION(linkedinIntegration: LinkedinIntegration) {
    this.linkedinIntegration = linkedinIntegration
  }

  @Mutation
  public SET_HRMS_INTEGRATION_TYPE(type: CompanyThirdPartyIntegrationsTypeEnum) {
    this.hrmsIntegrationType = type
  }

  @Mutation
  public SET_HRMS_INTEGRATIONS(type: CompanyThirdPartyIntegrationsItem[]) {
    this.hrmsIntegrations = type
  }

  @Mutation
  public SET_MEETING_BOT_RECORDING_INTEGRATION(support: boolean) {
    this.haveMeetingBotRecordingIntegration = support
  }

  @Mutation
  public SET_MEETING_BOT_AUTO_RECORDING_FLAG(flag: boolean) {
    // enable auto recording only when recording integration is set to true.
    if (this.haveMeetingBotRecordingIntegration) {
      this.haveMeetingBotAutoRecordFlag = flag
    }
  }

  @Action
  public setHrmsIntegrationType(type: CompanyThirdPartyIntegrationsTypeEnum) {
    this.SET_HRMS_INTEGRATION_TYPE(type)
  }

  @Action
  public UpdateCompanyLogo(logo: string) {
    this.SET_LOGO(logo)
  }

  @Action
  public setCompanyEmailSignature(emailSignature: string) {
    this.SET_COMPANY_EMAIL_SIGNATURE(emailSignature)
  }

  @Action
  private async getDynamicConfig(companyId: string): Promise<IDynamicConfigs[]> {
    try {
      const dynamicAppConfigs = new DynamicConfigsResource()
      const queryString = new URLSearchParams({
        companyId
      }).toString()

      const response = await dynamicAppConfigs.get(`?${queryString}`)
      return response.data as IDynamicConfigs[]
    } catch (e) {
      return []
    }
  }

  @Action
  public async setDynamicConfigurations(companyId: string) {
    const dynamicConfigs = await this.getDynamicConfig(companyId)
    const haveLanguageSupport = dynamicConfigs.find((each) => each.name === AppConfigEnum.LANGUAGE_SUPPORT)?.value === 'true'
    const haveMeetingBotRecordingIntegration = dynamicConfigs.find((each) => each.name === AppConfigEnum.MEETING_BOT_RECORDING_INTEGRATION)?.value === 'true'
    const haveMeetingBotAutoRecordFlag = dynamicConfigs.find((each) => each.name === AppConfigEnum.MEETING_BOT_AUTO_RECORD)?.value === 'true'
    const hasRequisitionsFeature = dynamicConfigs.find((each) => each.name === AppConfigEnum.REQUISITIONS)?.value === 'active'
    const haveCvParseAndScoreSupport = dynamicConfigs.find((each) => each.name === AppConfigEnum.CV_PARSE_AND_SCORE)?.value === 'true'

    this.SET_LANGUAGE_SUPPORT(haveLanguageSupport)
    this.SET_MEETING_BOT_RECORDING_INTEGRATION(haveMeetingBotRecordingIntegration)
    this.SET_MEETING_BOT_AUTO_RECORDING_FLAG(haveMeetingBotAutoRecordFlag)
    this.SET_CV_PARSE_AND_SCORE_SUPPORT(haveCvParseAndScoreSupport)
    const haveJobTargetIntegration = dynamicConfigs.find((each) => each.name === AppConfigEnum.JOB_TARGET_INTEGRATION)?.value === 'active'

    this.ENABLE_JOB_TARGET_INTEGRATION(haveJobTargetIntegration)
    this.SET_FEATURE_REQUISITIONS(hasRequisitionsFeature)
  }

  @Action
  public async setUpLinkedinIntegration(linkedinIntegration: LinkedinIntegration) {
    this.context.commit('SET_UP_LINKEDIN_INTEGRATION', linkedinIntegration)
  }

  @Action
  public async setJobTargetCompanyId(jobTargetCompanyId: number) {
    this.SET_JOB_TARGET_COMPANY_ID(jobTargetCompanyId)
  }

  @Action
  public async setReportsBetaTesting(companyId: string) {
    const haveSupport = await this.context.dispatch('getDynamicConfig', {
      companyId,
      configName: AppConfigEnum.REPORTS
    })
    this.context.commit('SET_REPORTS_BETA_TESTING', haveSupport)
  }

  @Action
  public async setHrmsIntegrations(integrations: CompanyThirdPartyIntegrationsItem[]) {
    this.SET_HRMS_INTEGRATIONS(integrations)
  }

  @Action
  public async getHrmsIntegrations() {
    return this.hrmsIntegrations
  }

  @Action
  public async setIsRequisitionEnabled(isRequisitionEnabled: boolean): Promise<void> {
    this.context.commit('SET_REQUISITION_ENABLE_STATUS', isRequisitionEnabled)
  }
}

export const CompanyModule = getModule(Company)
